import { ChangeIcon } from "allIcons";
import { useSites, useUpdateSite, useUpdateSiteURL } from "api/sites";
import {
  BodyContainer,
  DeleteButton,
  DeleteModal,
  Divider,
  HeaderContainer,
  InputField,
  Loader,
  LoadingCard,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  ToggleCard,
} from "commonUI";
import { isEqual, useSearch } from "helpers";
import useDeleteSite from "hooks/advanced";
import { t } from "i18next";
import { useEffect, useState } from "react";

export default function SiteSetting() {
  const { mutateAsync: deleteSite, isLoading: deletingSite } = useDeleteSite();
  const { web = null } = useSearch();
  const { data, isLoading } = useSites();
  const [siteData, setSiteData] = useState({});
  const [siteURL, setSiteURL] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { mutate: updateSite, isLoading: isUpdating } = useUpdateSite(true);
  const { mutate: updateSiteURL, isLoading: isUpdatingSiteURL } =
    useUpdateSiteURL();

  useEffect(() => {
    if (isLoading) return;
    const currentSiteDetails = data?.site_details?.total_sites.filter((item) =>
      isEqual(item.dl_website_id, web)
    )[0];
    setSiteData(currentSiteDetails);
    setSiteURL(currentSiteDetails?.web_url);
  }, [isLoading, web, data]);

  return (
    <>
      <RightMainContainer>
        <Loader show={deletingSite || isUpdatingSiteURL} loader="block" />
        <Loader show={isUpdating} loader={"block"} />
        <HeaderContainer>
          <PrimaryHeading>{t("siteSetting.advanced.title")}</PrimaryHeading>
        </HeaderContainer>
        <BodyContainer>
          <PrimaryHeading className="text-17">
            {t("siteSetting.generalSettings.siteDetails.title")}
          </PrimaryHeading>
          <SecondaryHeading className="mt-2">
            {t(
              "siteSetting.generalSettings.siteDetails.connectedWordpressSiteURL"
            )}
          </SecondaryHeading>
          {isLoading ? (
            <LoadingCard className="h-2 w-40 mt-2" />
          ) : (
            <>
              <p className="body-text mt-1 mb-3">{siteData?.web_url}</p>
              <div className="flex gap-10 items-center w-full  mt-1-5 relative">
                <InputField
                  value={siteData?.web_url}
                  inputDivClass="m-0"
                  className="m-0 flex-1"
                  placeholder="Write Site URL Here"
                  disabled
                />
                <div className="relative">
                  <MainButton
                    onClick={() => {
                      setShowConfirmation(true);
                    }}
                  >
                    <ChangeIcon />
                    {t(
                      "siteSetting.advanced.siteDetails.updateConnectedSite.buttonText"
                    )}
                  </MainButton>
                  {showConfirmation ? (
                    <>
                      <div className="delete-icon-layer" />
                      <DeleteModal
                        onClose={() => {
                          setShowConfirmation(false);
                        }}
                        setOpen={() => {
                          setShowConfirmation(false);
                        }}
                        open={showConfirmation}
                        deleteMessage={t(
                          "siteSetting.advanced.siteDetails.updateConnectedSite.popup.message"
                        )}
                        deleteButtonText={t(
                          "siteSetting.advanced.siteDetails.updateConnectedSite.popup.buttonText"
                        )}
                        deleteTitle={t(
                          "siteSetting.advanced.siteDetails.updateConnectedSite.popup.title"
                        )}
                        confirmInputTitle={t(
                          "siteSetting.advanced.siteDetails.updateConnectedSite.popup.inputTitle"
                        )}
                        deteleConfirmationsClasses="delete-button-red"
                        onDelete={async () => {
                          updateSiteURL({
                            website_id: web,
                            website_url: siteURL,
                          });
                        }}
                        deleteConfirmations={siteData?.web_url}
                        inputValue={siteURL}
                        setInputValue={(value) => setSiteURL(value)}
                      />
                    </>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </>
          )}

          <SecondaryHeading className="mt-2">
            {t("siteSetting.generalSettings.siteDetails.diskUsage")}
          </SecondaryHeading>
          {isLoading ? (
            <LoadingCard className="h-2 w-40 mt-2" />
          ) : (
            <p className="body-text mt-1">
              {t("siteSetting.generalSettings.siteDetails.diskUsageInGB", {
                size: siteData?.single_web_storage,
              })}
            </p>
          )}
          <SecondaryHeading className="mt-2">
            {t(
              "siteSetting.generalSettings.siteDetails.enableWPSiteLauncher.title"
            )}
          </SecondaryHeading>
          {isLoading ? (
            <>
              <LoadingCard className="h-2 w-full mt-2" />
              <LoadingCard className="h-2 w-1/2 mt-2" />
            </>
          ) : (
            <p className="body-text mt-1">
              {t(
                "siteSetting.generalSettings.siteDetails.enableWPSiteLauncher.description"
              )}
            </p>
          )}
          <ToggleCard
            isLoading={isLoading}
            value={
              siteData?.status !== null ? isEqual(siteData?.status, 1) : true
            }
            setValue={(value) => {
              setSiteData({
                ...siteData,
                status: value === true ? "1" : "0",
              });
              updateSite({
                website_key: siteData.dl_website_id,
                status: value === true ? "1" : "0",
              });
            }}
          />
          <Divider height={2} className="mt-5 mb-3" />
          <PrimaryHeading className="text-17">
            {t("siteSetting.advanced.siteData.title")}
          </PrimaryHeading>
          {/* <SecondaryHeading className="mt-2">
            Override Site Settings With The New Site Default Configuration
            Settings
          </SecondaryHeading>
          <p className="body-text text-theme-placeholder mt-1">
            Choose to reset all the local plugin settings and replace them with
            the default site configuration settings.
          </p>
          <MainButton>
            <ResetIcon size="12" /> Reset Site Settings
          </MainButton>
          <SecondaryHeading className="mt-2">
            Import & Export Site Data
          </SecondaryHeading>
          <p className="body-text mt-1">
            The WP SiteLauncher site data can be exported and imported to act as
            a backup and restore method. Please be careful when importing data,
            as it will override the exiting site data.
          </p>
          <div className="flex gap-7 mt-2 mb-2">
            <MainButton>
              <ExportIcon /> Export Site Data
            </MainButton>
            <MainButton>
              <ImportIcon /> Import Site Data
            </MainButton>
          </div>
          <SecondaryHeading className="mt-2">Delete Site Data</SecondaryHeading>
          <p className="body-text mt-1">
            If you are no longer using WP SiteLauncher on this site, you can
            choose to delete the data in order to free up storage space in your
            account.. You can choose to export the site data first as a backup
            for peace of mind.
          </p>
          <MainButton className="delete-button-red">
            <DeleteIcon fill="white" />
            Delete Site Data
          </MainButton> */}
          <div className="mt-2">
            {/* <SecondaryHeading className="mt-2">Delete Site</SecondaryHeading>
            <p className="body-text mt-1">
              If you no longer need this site, you can choose to delete it from
              your account. This will permanently remove the site and all of its
              associated data.
            </p>
            <MainButton
              className="delete-button-red"
              disabled={isLoading}
              onClick={() => {
                deleteSite({
                  web_url: siteDetails.web_url,
                  website_id: siteDetails.dl_website_id,
                });
              }}
            >
              <DeleteIcon fill="white" />
              Delete Site
            </MainButton> */}
            <DeleteButton
              type="button"
              buttonText={t(
                "siteSetting.advanced.siteData.deleteSite.buttonText"
              )}
              title={t("siteSetting.advanced.siteData.deleteSite.title")}
              message={t("siteSetting.advanced.siteData.deleteSite.message")}
              deleteMessage={t(
                "siteSetting.advanced.siteData.deleteSiteConfirmation.message"
              )}
              deleteButtonText={t("common.popup.button.confirmDelete")}
              onDelete={async () => {
                deleteSite({
                  web_url: siteData.web_url,
                  website_id: siteData.dl_website_id,
                });
              }}
              style={{
                left: "0",
                right: "auto",
                bottom: "calc(100% + 5px)",
                top: "auto",
              }}
            />
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
